/**
 * ui-settings-store — R232q20 Phase 1 Foundation.
 *
 * Zustand-persisted slice for opt-in HUD panels and player UI preferences.
 * Mirrors WoW 3.3.5a "Interface Options → ActionBars / Display / Combat
 * Text / Names" — every toggleable HUD piece lives here so users can keep
 * a minimal screen until they explicitly enable extra bars/meters.
 *
 * Storage: single localStorage key `aiwari-ui-settings`. Every setter
 * writes through synchronously so a tab-reload restores the exact state.
 *
 * Defaults — chosen to match a fresh WoW 3.3.5a client:
 *   ActionBars (Right1/Right2/BottomLeft/BottomRight)  — OFF
 *   CastBar / XpBar / RepBar                            — ON
 *   LatencyMeter / Fps                                  — OFF
 *   uiScale 1.0, chatFontSize 13, combatTextSize 22
 *   forceMobileUi = false (let the touch detector decide)
 *
 * NOTE: this store deliberately does NOT contain the player's
 * keybindings (see keybinds-store.ts) or hotbar slot bindings
 * (see hotbar-store.ts). Those are richer per-class structures. This
 * file only owns binary toggles + a few scalar prefs.
 */

import { create } from 'zustand';

const STORAGE_KEY = 'aiwari-ui-settings';

export interface UiSettings {
  // ── Action bar opt-ins (per WoW 3.3.5a "Interface Options → ActionBars")
  /** Vertical bar pinned to the right edge of the screen. */
  showRightBar1: boolean;
  /** Vertical bar inside RightBar1 (closer to the centre). */
  showRightBar2: boolean;
  /** Horizontal 12-slot bar below the main hotbar. */
  showBottomLeft: boolean;
  /** Horizontal 12-slot bar below RightBar1/2 / BottomLeft. */
  showBottomRight: boolean;

  // ── HUD frames
  showCastBar: boolean;
  showXpBar: boolean;
  showRepBar: boolean;
  showLatencyMeter: boolean;
  showFps: boolean;

  // ── Misc scalars
  /** UI master scale. Clamped to 0.75–1.5. */
  uiScale: number;
  /** Chat window font size in px. Clamped to 11–18. */
  chatFontSize: number;
  /** Floating combat text font size in px. Clamped to 14–32. */
  combatTextSize: number;

  // ── Mobile
  /** Override touch detection — force the mobile/touch HUD on a desktop. */
  forceMobileUi: boolean;
}

export interface UiSettingsStore extends UiSettings {
  /** Strongly typed single-key setter. Persists to localStorage. */
  set: <K extends keyof UiSettings>(k: K, v: UiSettings[K]) => void;
  /** Restore every field to its default and clear localStorage. */
  reset: () => void;
}

const DEFAULTS: UiSettings = {
  showRightBar1: false,
  showRightBar2: false,
  showBottomLeft: false,
  showBottomRight: false,
  showCastBar: true,
  showXpBar: true,
  showRepBar: true,
  showLatencyMeter: false,
  showFps: false,
  uiScale: 1.0,
  chatFontSize: 13,
  combatTextSize: 22,
  forceMobileUi: false,
};

/** Numeric keys with their min/max for sanity-clamping on load + set. */
const SCALAR_BOUNDS: Partial<Record<keyof UiSettings, [number, number]>> = {
  uiScale: [0.75, 1.5],
  chatFontSize: [11, 18],
  combatTextSize: [14, 32],
};

function clampScalar<K extends keyof UiSettings>(
  key: K,
  value: UiSettings[K],
): UiSettings[K] {
  const bounds = SCALAR_BOUNDS[key];
  if (!bounds || typeof value !== 'number' || !Number.isFinite(value)) return value;
  const [lo, hi] = bounds;
  const clamped = Math.min(hi, Math.max(lo, value));
  return clamped as UiSettings[K];
}

function load(): UiSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<UiSettings>;
    const merged: UiSettings = { ...DEFAULTS };
    for (const k of Object.keys(DEFAULTS) as (keyof UiSettings)[]) {
      const incoming = parsed[k];
      if (incoming === undefined || incoming === null) continue;
      // Type-guard: only adopt the value if its typeof matches the default.
      if (typeof incoming !== typeof DEFAULTS[k]) continue;
      // Cast is safe — we just verified typeof equality with the default.
      (merged as Record<keyof UiSettings, UiSettings[keyof UiSettings]>)[k] =
        clampScalar(k, incoming as UiSettings[keyof UiSettings]);
    }
    return merged;
  } catch {
    return { ...DEFAULTS };
  }
}

function save(state: UiSettings) {
  try {
    // Strip the action methods before persisting — only data fields matter.
    const data: UiSettings = {
      showRightBar1: state.showRightBar1,
      showRightBar2: state.showRightBar2,
      showBottomLeft: state.showBottomLeft,
      showBottomRight: state.showBottomRight,
      showCastBar: state.showCastBar,
      showXpBar: state.showXpBar,
      showRepBar: state.showRepBar,
      showLatencyMeter: state.showLatencyMeter,
      showFps: state.showFps,
      uiScale: state.uiScale,
      chatFontSize: state.chatFontSize,
      combatTextSize: state.combatTextSize,
      forceMobileUi: state.forceMobileUi,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* localStorage quota / private mode — ignore. */
  }
}

export const useUiSettings = create<UiSettingsStore>((set, get) => ({
  ...load(),
  set: (k, v) => {
    const clamped = clampScalar(k, v);
    set({ [k]: clamped } as Partial<UiSettingsStore>);
    save(get());
  },
  reset: () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    set({ ...DEFAULTS });
  },
}));

/** Convenience: read the persisted defaults (useful in unit tests). */
export const UI_SETTINGS_DEFAULTS: Readonly<UiSettings> = Object.freeze({ ...DEFAULTS });
